class VideoMedia extends HTMLElement {
  constructor() {
    super();
    this.nativeVideos = this.querySelectorAll('video');
    this.youtubeIframe = this.querySelector('iframe');

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.muteEnabled = this.dataset.mute === 'true';
    this.loopEnabled = this.dataset.loop === 'true';

    this.playerReady = false;
    this.player = null;

    if (this.youtubeIframe) {
      this.initYouTubeAPI();
    } else if (this.nativeVideos.length > 0) {
      this.initNativeVideo();
    }
  }

  initNativeVideo() {
    // If autoplay is off, we leave it alone.
    // If autoplay is true, Shopify's 'autoplay: true' filter handles it natively,
    // but we can use IntersectionObserver to pause when out of view to save resources.
    if (!this.autoplayEnabled) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.nativeVideos.forEach((video) => {
            if (entry.isIntersecting) {
              video.play().catch((e) => console.warn('Autoplay blocked:', e));
            } else {
              video.pause();
            }
          });
        });
      },
      { threshold: 0.1 },
    );

    this.observer.observe(this);
  }

  initYouTubeAPI() {
    // Load YouTube API script if not already present
    if (
      !document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]',
      )
    ) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Wait for the global callback if the API isn't ready
    if (window.YT && window.YT.Player) {
      this.initYouTubePlayer();
    } else {
      const originalCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (originalCallback) originalCallback();
        document.querySelectorAll('video-media iframe').forEach((iframe) => {
          const parent = iframe.closest('video-media');
          if (parent && !parent.playerReady) parent.initYouTubePlayer();
        });
      };
    }
  }

  initYouTubePlayer() {
    if (this.playerReady) return;

    this.player = new YT.Player(this.youtubeIframe, {
      events: {
        onReady: this.onPlayerReady.bind(this),
      },
    });

    this.playerReady = true;
  }

  onPlayerReady(event) {
    if (this.muteEnabled) {
      event.target.mute();
    }

    // Use intersection observer to pause/play YouTube iframe when scrolling
    if (this.autoplayEnabled) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              event.target.playVideo();
            } else {
              event.target.pauseVideo();
            }
          });
        },
        { threshold: 0.1 },
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }
  }
}

customElements.define('video-media', VideoMedia);
